import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import { Edit as EditIcon, Delete as DeleteIcon } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import { useJob } from '../context/JobContext';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Handshake as CounterpartyLogoIcon } from 'lucide-react';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, icon: Icon, color, active, onClick }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? color : undefined,
      bgcolor: active ? `${color}08` : 'background.paper',
      transition: 'border-color 0.2s ease, background-color 0.2s ease',
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color: color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

// Constants
const TIPI_CONTROPARTE = [
  { value: 'banca', label: 'Banca' },
  { value: 'finanziaria', label: 'Finanziaria' },
  { value: 'servicer', label: 'Servicer' },
  { value: 'cessionario', label: 'Cessionario' },
  { value: 'legale', label: 'Legale' },
  { value: 'altro', label: 'Altro' }
];

const validationSchema = Yup.object({
  denominazione: Yup.string().required('Denominazione obbligatoria'),
  tipo: Yup.string().required('Tipo obbligatorio'),
  email: Yup.string().email('Email non valida').nullable(),
  pec: Yup.string().email('PEC non valida').nullable(),
  telefono: Yup.string().nullable(),
  referente_nome: Yup.string().nullable(),
  referente_email: Yup.string().email('Email referente non valida').nullable(),
  referente_telefono: Yup.string().nullable(),
  note: Yup.string().nullable()
});

export default function Controparti() {
  // State
  const [controparti, setControparti] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');
  
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [selected, setSelected] = useState([]);
  const { addJob } = useJob();
  const [stats, setStats] = useState({ banca: 0, finanziaria: 0, servicer: 0, cessionario: 0, legale: 0, altro: 0 });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [counterpartyToDelete, setCounterpartyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch Data
  const fetchControparti = React.useCallback(async () => {
    try {
      const params = {
        search,
        type: filterType !== 'all' ? filterType : undefined,
        page: page + 1,
        limit: rowsPerPage,
        sort: orderBy,
        order: orderDirection,
        _t: new Date().getTime()
      };
      
      const res = await api.get('/controparti', { params });
      
      if (res.data && Array.isArray(res.data.controparti)) {
        setControparti(res.data.controparti);
        setTotal(res.data.totalItems || res.data.total);
      } else {
        setControparti([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching controparti:', error);
      toast.error('Errore nel caricamento delle controparti');
    }
  }, [search, filterType, page, rowsPerPage, orderBy, orderDirection]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/controparti/stats');
      if (res.data) {
        setStats(res.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchControparti();
    fetchStats();
  }, [fetchControparti]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = controparti.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePrintPdf = async () => {
    try {
      const startRes = await api.post('/controparti/report', { ids: selected });
      const jobId = startRes.data.jobId;
      addJob(jobId, 'controparte');
      setSelected([]);
    } catch (error) {
      console.error('Error starting report job:', error);
      toast.error('Errore nell\'avvio della generazione del PDF');
    }
  };

  const handleDeleteClick = (controparte) => {
    setCounterpartyToDelete(controparte);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!counterpartyToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/controparti/${counterpartyToDelete.id}`);
      toast.success('Controparte eliminata con successo');
      fetchControparti();
      fetchStats();
      setDeleteDialogOpen(false);
      setCounterpartyToDelete(null);
    } catch (error) {
      console.error(error);
      toast.error('Errore nell\'eliminazione della controparte');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCounterpartyToDelete(null);
  };

  const handleWidgetClick = (type) => {
    if (filterType === type) {
      setFilterType('all');
    } else {
      setFilterType(type);
    }
    setPage(0);
  };

  // Formik
  const formik = useFormik({
    initialValues: {
      denominazione: '',
      tipo: 'altro',
      email: '',
      pec: '',
      telefono: '',
      referente_nome: '',
      referente_email: '',
      referente_telefono: '',
      note: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        if (currentId) {
          await api.put(`/controparti/${currentId}`, values);
          toast.success('Controparte aggiornata con successo');
        } else {
          await api.post('/controparti', values);
          toast.success('Controparte creata con successo');
        }
        handleClose();
        fetchControparti();
        fetchStats();
      } catch (error) {
        console.error('Submit error:', error);
        toast.error('Errore nel salvataggio della controparte');
      }
    },
  });

  const handleOpen = (controparte = null) => {
    if (controparte) {
      setCurrentId(controparte.id);
      formik.setValues({
        denominazione: controparte.denominazione || '',
        tipo: controparte.tipo || 'altro',
        email: controparte.email || '',
        pec: controparte.pec || '',
        telefono: controparte.telefono || '',
        referente_nome: controparte.referente_nome || '',
        referente_email: controparte.referente_email || '',
        referente_telefono: controparte.referente_telefono || '',
        note: controparte.note || ''
      });
    } else {
      setCurrentId(null);
      formik.resetForm();
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentId(null);
    formik.resetForm();
  };

  // Helpers
  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'banca': return 'primary';
      case 'finanziaria': return 'secondary';
      case 'servicer': return 'warning';
      case 'cessionario': return 'info';
      case 'legale': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CounterpartyLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Gestione Controparti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica banche, finanziarie, servicer e legali
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintPdf}
            disabled={selected.length === 0}
            sx={{
              borderRadius: '10px !important',
              textTransform: 'none',
              px: 2.5,
              height: 40,
              minWidth: 124,
              borderColor: '#94a3b8',
              color: '#334155',
              bgcolor: 'transparent',
              '&:hover': {
                borderColor: '#64748b',
                bgcolor: '#f8fafc'
              },
              '&.Mui-disabled': {
                borderColor: '#cbd5e1',
                color: '#9ca3af',
                bgcolor: 'transparent'
              }
            }}
          >
            Stampa PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              borderRadius: '10px !important',
              textTransform: 'none',
              px: 2.5,
              height: 40,
              minWidth: 170,
              bgcolor: '#0f172a',
              '&:hover': {
                bgcolor: '#1e293b'
              }
            }}
          >
            Nuova Controparte
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Banche"
            value={stats.banca || 0}
            icon={AccountBalanceIcon}
            color="#0ea5e9"
            active={filterType === 'banca'}
            onClick={() => handleWidgetClick('banca')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Finanziarie"
            value={stats.finanziaria || 0}
            icon={BusinessIcon}
            color="#6366f1"
            active={filterType === 'finanziaria'}
            onClick={() => handleWidgetClick('finanziaria')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Servicer"
            value={stats.servicer || 0}
            icon={AttachMoneyIcon}
            color="#f59e0b"
            active={filterType === 'servicer'}
            onClick={() => handleWidgetClick('servicer')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Legali"
            value={stats.legale || 0}
            icon={GavelIcon}
            color="#ef4444"
            active={filterType === 'legale'}
            onClick={() => handleWidgetClick('legale')}
          />
        </Grid>
      </Grid>

      <StyledCard sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Cerca controparti..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filterType}
                  label="Tipo"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  {TIPI_CONTROPARTE.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      <StyledCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < controparti.length}
                    checked={controparti.length > 0 && selected.length === controparti.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'denominazione'}
                    direction={orderBy === 'denominazione' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('denominazione')}
                  >
                    Denominazione
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'tipo'}
                    direction={orderBy === 'tipo' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('tipo')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>Contatti</TableCell>
                <TableCell>Referente</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {controparti.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessuna controparte trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                controparti.map((row) => {
                  const isItemSelected = selected.indexOf(row.id) !== -1;
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      selected={isItemSelected}
                      sx={{ '&.Mui-selected': { bgcolor: 'action.selected' } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={(event) => handleClick(event, row.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {row.denominazione}
                        </Typography>
                        {row.note && (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 200 }}>
                            {row.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.tipo}
                          color={getTipoColor(row.tipo)}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {row.email && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              📧 {row.email}
                            </Typography>
                          )}
                          {row.pec && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              📯 {row.pec}
                            </Typography>
                          )}
                          {row.telefono && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              📞 {row.telefono}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.referente_nome ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.referente_nome}
                            </Typography>
                            {row.referente_email && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.referente_email}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpen(row)} color="primary">
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(row)} color="error">
                          <DeleteIcon size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="counterparty-delete-dialog-title"
        aria-describedby="counterparty-delete-dialog-description"
      >
        <DialogTitle id="counterparty-delete-dialog-title">
          {"Conferma eliminazione controparte"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="counterparty-delete-dialog-description">
            Sei sicuro di voler eliminare la controparte
            {counterpartyToDelete?.denominazione ? ` "${counterpartyToDelete.denominazione}"` : ''}?
            Questa azione è irreversibile.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Annulla
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
            {currentId ? 'Modifica Controparte' : 'Nuova Controparte'}
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  id="denominazione"
                  name="denominazione"
                  label="Denominazione"
                  value={formik.values.denominazione}
                  onChange={formik.handleChange}
                  error={formik.touched.denominazione && Boolean(formik.errors.denominazione)}
                  helperText={formik.touched.denominazione && formik.errors.denominazione}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="tipo-label">Tipo</InputLabel>
                  <Select
                    labelId="tipo-label"
                    id="tipo"
                    name="tipo"
                    value={formik.values.tipo}
                    label="Tipo"
                    onChange={formik.handleChange}
                    error={formik.touched.tipo && Boolean(formik.errors.tipo)}
                  >
                    {TIPI_CONTROPARTE.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="pec"
                  name="pec"
                  label="PEC"
                  value={formik.values.pec}
                  onChange={formik.handleChange}
                  error={formik.touched.pec && Boolean(formik.errors.pec)}
                  helperText={formik.touched.pec && formik.errors.pec}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="telefono"
                  name="telefono"
                  label="Telefono"
                  value={formik.values.telefono}
                  onChange={formik.handleChange}
                  error={formik.touched.telefono && Boolean(formik.errors.telefono)}
                  helperText={formik.touched.telefono && formik.errors.telefono}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Referente</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id="referente_nome"
                  name="referente_nome"
                  label="Nome Referente"
                  value={formik.values.referente_nome}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id="referente_email"
                  name="referente_email"
                  label="Email Referente"
                  value={formik.values.referente_email}
                  onChange={formik.handleChange}
                  error={formik.touched.referente_email && Boolean(formik.errors.referente_email)}
                  helperText={formik.touched.referente_email && formik.errors.referente_email}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id="referente_telefono"
                  name="referente_telefono"
                  label="Telefono Referente"
                  value={formik.values.referente_telefono}
                  onChange={formik.handleChange}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="note"
                  name="note"
                  label="Note"
                  multiline
                  rows={3}
                  value={formik.values.note}
                  onChange={formik.handleChange}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                  Annulla
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {currentId ? 'Salva Modifiche' : 'Crea Controparte'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
    </Box>
  );
}
